import { useState, forwardRef } from 'react';
import { Box, ActionIcon, Group, useMantineTheme, Button, useMantineColorScheme } from '@mantine/core';
import { IconEdit, IconCheck } from '@tabler/icons-react';
import HtmlEditor, { HtmlEditorProps } from './HtmlEditor';

export interface CompactHtmlEditorProps extends Omit<HtmlEditorProps, 'showToolbar'> {
  compactHeight?: number | string;
}

/**
 * CompactHtmlEditor - A textarea-like variant of HtmlEditor that shows a compact
 * text area initially and displays editing controls on hover or when the edit button is clicked.
 */
const CompactHtmlEditor = forwardRef<HTMLDivElement, CompactHtmlEditorProps>(
  (
    {
      value = '',
      onChange,
      placeholder = 'Start typing...',
      minHeight = 100,
      compactHeight = 100,
      ...rest
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const previewBorder = isDark ? theme.colors.dark[4] : theme.colors.gray[3];
    const previewHoverBorder = isDark ? theme.colors.dark[3] : theme.colors.gray[4];
    const previewBg = isDark ? theme.colors.dark[7] : theme.white;
    const previewText = isDark ? theme.colors.gray[2] : theme.colors.dark[7];
    const placeholderColor = isDark ? theme.colors.dark[2] : theme.colors.gray[5];
    const [isExpanded, setIsExpanded] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Box
        ref={ref}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ position: 'relative' }}
      >
        {!isExpanded ? (
          <Box
            style={{
              position: 'relative',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: isHovered ? previewHoverBorder : previewBorder,
              borderRadius: theme.radius.sm,
              padding: theme.spacing.md,
              minHeight: typeof compactHeight === 'number' ? `${compactHeight}px` : compactHeight,
              backgroundColor: previewBg,
              cursor: 'text',
              transition: 'all 0.2s ease',
              ...(isHovered && {
                boxShadow: theme.shadows.sm,
              }),
            }}
            onClick={() => setIsExpanded(true)}
          >
            {/* Read-only preview */}
            <Box
              dangerouslySetInnerHTML={{ __html: value || `<p style="color: ${placeholderColor};">${placeholder}</p>` }}
              style={{
                fontSize: '14px',
                lineHeight: 1.6,
                color: value ? previewText : placeholderColor,
                wordWrap: 'break-word',
              }}
            />

            {/* Hover controls */}
            {isHovered && (
              <Box
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 10,
                }}
              >
                <ActionIcon
                  variant="filled"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(true);
                  }}
                >
                  <IconEdit size={14} />
                </ActionIcon>
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <HtmlEditor
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              minHeight={minHeight}
              showToolbar={true}
              {...rest}
            />
            <Group justify="flex-end" mt="md" gap="xs">
              <Button
                variant="filled"
                color="gray"
                leftSection={<IconCheck size={16} />}
                onClick={() => setIsExpanded(false)}
                size="sm"
              >
                Done Editing
              </Button>
            </Group>
          </Box>
        )}
      </Box>
    );
  }
);

CompactHtmlEditor.displayName = 'CompactHtmlEditor';

export default CompactHtmlEditor;
